// KPI Builder — question analytics. Shapes of the live “Question-Item Difficulty” and “Question Quality by Cohort”.
import { INDEX_PREFIX, SOURCES } from 'constants/kpiBuilderCatalogue';
import { envelope, filterFor, RecipeContext, RecipeResult } from './queries';
import { EsNode, QueryObject } from './types';

const PCT_SCRIPT = 'params.t>0?Math.round(params.c*1000.0/params.t)/10.0:0';
const pairScript = (competencyField: string) => ({ source: `doc['userId'].value+'_'+doc['${competencyField}'].value`, lang: 'painless' });

// Share of answers that are correct, per question; the dashboard sorts hardest first.
function buildDifficulty(c: RecipeContext): RecipeResult {
  const perQuestion: EsNode = {
    terms: { field: 'questionId', size: 500 },
    aggs: {
      correct_count: { filter: { term: { chosenOptionIsCorrect: true } } },
      total_count: { filter: { match_all: {} } },
      pct_correct: { bucket_script: { buckets_path: { c: 'correct_count>_count', t: 'total_count>_count' }, script: PCT_SCRIPT } },
    },
  };
  const aggs = { filtered_data: { filter: filterFor(c.srcId, c.a), aggs: { by_question: perQuestion } } };
  return {
    queries: [{ ...envelope(c.srcId, c.a), filterPath: 'aggregations.filtered_data.by_question.buckets', aggrQuery: { size: 0, aggs } }],
    cfg: { chartType: 'questiondifficulty', isDecimal: true, aggregationPaths: ['filtered_data', 'by_question'] },
    handlerKey: 'questiondifficulty',
  };
}

// First-attempt correctness per question and learner, next to the learners who eventually passed.
function buildCohort(c: RecipeContext): RecipeResult {
  const first = filterFor(c.srcId, c.a);
  const firstFilters = [{ term: { attemptNumber: 1 } }, ...((first as { bool?: { filter: unknown[] } }).bool?.filter ?? [])];
  const questions = { questionAgg: { filter: { bool: { filter: firstFilters } }, aggs: { by_question: {
    terms: { field: 'questionId', size: 300 },
    aggs: { pairs: { terms: { script: pairScript('competencyId'), size: 3000 }, aggs: { is_correct: { avg: { field: 'chosenOptionIsCorrect' } } } } },
  } } } };
  const passbook = { passbookAgg: { filter: filterFor('pass', c.a, true), aggs: { pairs: { terms: { script: pairScript('competencyID'), size: 20000 } } } } };
  const passEnvelope = envelope('pass', c.a);
  delete passEnvelope.requestQueryMap.Course; // the passbook side is matched by competency only
  const queries: QueryObject[] = [
    { ...envelope(c.srcId, c.a), filterPath: 'aggregations', aggrQuery: { size: 0, aggs: questions } },
    { ...passEnvelope, indexName: INDEX_PREFIX + SOURCES.pass.index, filterPath: 'aggregations', aggrQuery: { size: 0, aggs: passbook } },
  ];
  return {
    queries,
    cfg: { chartType: 'questionqualitycohort', valueType: 'percentage', categoryLabel: 'Question', aggregationPaths: ['questionAgg', 'passbookAgg'] },
    handlerKey: 'questionqualitycohort',
  };
}

export const buildQuestions = (c: RecipeContext): RecipeResult => (c.a.questionKind === 'cohort' ? buildCohort(c) : buildDifficulty(c));
